import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import type { ReactNode } from "react";
import { AuthProvider, useAuth } from "./lib/auth-context";
import { LoginPage } from "./pages/Login";
import { ChangePasswordPage } from "./pages/ChangePassword";
import { DashboardPage } from "./pages/Dashboard";
import { SecurityPage } from "./pages/Security";
import { TenantsPage } from "./pages/Tenants";
import { OnboardingPage } from "./pages/Onboarding";
import { PlatformMetricsPage } from "./pages/PlatformMetrics";
import { TenantCanalesPage } from "./pages/TenantCanales";
import { TenantDetailPage } from "./pages/TenantDetail";
import { TenantWhatsappPage } from "./pages/TenantWhatsapp";
import { PlatformConfigPage } from "./pages/PlatformConfig";
import { PlatformEncuestaBasePage } from "./pages/PlatformEncuestaBase";
import { TenantAIDraftPage } from "./pages/TenantAIDraft";
import { TenantSoporteRagPage } from "./pages/TenantSoporteRag";
import { TenantServiciosPage } from "./pages/TenantServicios";
import { DocsPage } from "./pages/Docs";
import { DocsAdminTenantPage } from "./pages/DocsAdminTenant";
import { DocsCitasPage } from "./pages/DocsCitas";
import { DocsEliminarTenantPage } from "./pages/DocsEliminarTenant";
import { DocsGestionDocumentalPage } from "./pages/DocsGestionDocumental";
import { DocsHabeasDataPage } from "./pages/DocsHabeasData";
import { DocsOnboardingPage } from "./pages/DocsOnboarding";
import { DocsOperadorPage } from "./pages/DocsOperador";
import { DocsPqrsdfPage } from "./pages/DocsPqrsdf";
import { DocsProveedoresIAPage } from "./pages/DocsProveedoresIA";
import { DocsRadicacionPage } from "./pages/DocsRadicacion";
import { DocsRetencionPage } from "./pages/DocsRetencion";
import { DocsRunbookPage } from "./pages/DocsRunbook";
import { DocsSupervisorPage } from "./pages/DocsSupervisor";
import { DocsTerminosPage } from "./pages/DocsTerminos";
import { DocsWhatsappPage } from "./pages/DocsWhatsapp";
import { TenantBrandingPage } from "./pages/TenantBranding";
import { TenantEncuestasPage } from "./pages/TenantEncuestas";
import { TenantSLAPage } from "./pages/TenantSLA";
import { TenantSigningPage } from "./pages/TenantSigning";
import { TenantFuncionariosPage } from "./pages/TenantFuncionarios";
import { AuditLogPage } from "./pages/AuditLog";

function Loading() {
  return (
    <div className="auth-stage">
      <div className="eyebrow">Cargando…</div>
    </div>
  );
}

function Guard({ children }: { children: ReactNode }) {
  const { loading, user } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return <>{children}</>;
}

function RedirectToServicios() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/tenants/${id}/servicios`} replace />;
}

function RedirectIfAuth({ children }: { children: ReactNode }) {
  const { loading, user } = useAuth();
  if (loading) return <Loading />;
  if (user) {
    return (
      <Navigate
        to={user.must_change_password ? "/change-password" : "/"}
        replace
      />
    );
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              <RedirectIfAuth>
                <LoginPage />
              </RedirectIfAuth>
            }
          />
          <Route
            path="/change-password"
            element={
              <Guard>
                <ChangePasswordPage />
              </Guard>
            }
          />
          <Route
            path="/tenants"
            element={
              <Guard>
                <TenantsPage />
              </Guard>
            }
          />
          <Route
            path="/onboarding"
            element={
              <Guard>
                <OnboardingPage />
              </Guard>
            }
          />
          <Route
            path="/platform/metrics"
            element={
              <Guard>
                <PlatformMetricsPage />
              </Guard>
            }
          />
          <Route
            path="/tenants/:id/funcionarios"
            element={
              <Guard>
                <TenantFuncionariosPage />
              </Guard>
            }
          />
          <Route
            path="/tenants/:id/whatsapp"
            element={
              <Guard>
                <TenantWhatsappPage />
              </Guard>
            }
          />
          <Route
            path="/tenants/:id/canales"
            element={
              <Guard>
                <TenantCanalesPage />
              </Guard>
            }
          />
          <Route
            path="/tenants/:id/branding"
            element={
              <Guard>
                <TenantBrandingPage />
              </Guard>
            }
          />
          <Route
            path="/tenants/:id/ai-draft"
            element={
              <Guard>
                <TenantAIDraftPage />
              </Guard>
            }
          />
          <Route
            path="/tenants/:id/soporte-rag"
            element={
              <Guard>
                <TenantSoporteRagPage />
              </Guard>
            }
          />
          <Route
            path="/tenants/:id/servicios"
            element={
              <Guard>
                <TenantServiciosPage />
              </Guard>
            }
          />
          {/* Sub-hub legacy de GD: ahora todo vive en /servicios.
              Mantenemos la ruta para no romper bookmarks; redirige. */}
          <Route
            path="/tenants/:id/gestion-documental"
            element={<RedirectToServicios />}
          />
          <Route
            path="/tenants/:id/encuestas"
            element={
              <Guard>
                <TenantEncuestasPage />
              </Guard>
            }
          />
          <Route
            path="/tenants/:id/sla"
            element={
              <Guard>
                <TenantSLAPage />
              </Guard>
            }
          />
          <Route
            path="/tenants/:id/signing"
            element={
              <Guard>
                <TenantSigningPage />
              </Guard>
            }
          />
          <Route
            path="/platform/config"
            element={
              <Guard>
                <PlatformConfigPage />
              </Guard>
            }
          />
          <Route
            path="/platform/encuesta-base"
            element={
              <Guard>
                <PlatformEncuestaBasePage />
              </Guard>
            }
          />
          <Route
            path="/tenants/:id"
            element={
              <Guard>
                <TenantDetailPage />
              </Guard>
            }
          />
          <Route
            path="/audit-log"
            element={
              <Guard>
                <AuditLogPage />
              </Guard>
            }
          />
          <Route
            path="/security"
            element={
              <Guard>
                <SecurityPage />
              </Guard>
            }
          />
          <Route
            path="/"
            element={
              <Guard>
                <DashboardPage />
              </Guard>
            }
          />
          <Route
            path="/docs"
            element={
              <Guard>
                <DocsPage />
              </Guard>
            }
          />
          <Route
            path="/docs/onboarding"
            element={
              <Guard>
                <DocsOnboardingPage />
              </Guard>
            }
          />
          <Route
            path="/docs/whatsapp"
            element={
              <Guard>
                <DocsWhatsappPage />
              </Guard>
            }
          />
          <Route
            path="/docs/proveedores-ia"
            element={
              <Guard>
                <DocsProveedoresIAPage />
              </Guard>
            }
          />
          <Route
            path="/docs/runbook"
            element={
              <Guard>
                <DocsRunbookPage />
              </Guard>
            }
          />
          <Route
            path="/docs/eliminar-tenant"
            element={
              <Guard>
                <DocsEliminarTenantPage />
              </Guard>
            }
          />
          <Route
            path="/docs/admin-tenant"
            element={
              <Guard>
                <DocsAdminTenantPage />
              </Guard>
            }
          />
          <Route
            path="/docs/operador"
            element={
              <Guard>
                <DocsOperadorPage />
              </Guard>
            }
          />
          <Route
            path="/docs/supervisor"
            element={
              <Guard>
                <DocsSupervisorPage />
              </Guard>
            }
          />
          <Route
            path="/docs/habeas-data"
            element={
              <Guard>
                <DocsHabeasDataPage />
              </Guard>
            }
          />
          <Route
            path="/docs/terminos"
            element={
              <Guard>
                <DocsTerminosPage />
              </Guard>
            }
          />
          <Route
            path="/docs/retencion"
            element={
              <Guard>
                <DocsRetencionPage />
              </Guard>
            }
          />
          <Route
            path="/docs/radicacion"
            element={
              <Guard>
                <DocsRadicacionPage />
              </Guard>
            }
          />
          <Route
            path="/docs/pqrsdf"
            element={
              <Guard>
                <DocsPqrsdfPage />
              </Guard>
            }
          />
          <Route
            path="/docs/citas"
            element={
              <Guard>
                <DocsCitasPage />
              </Guard>
            }
          />
          <Route
            path="/docs/gestion-documental"
            element={
              <Guard>
                <DocsGestionDocumentalPage />
              </Guard>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
